import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Calculator, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatBot from '../components/ChatBot';
import QuoteServiceRow from '../components/quote/QuoteServiceRow';
import QuoteSummary from '../components/quote/QuoteSummary';
import QuoteRequestDialog from '../components/quote/QuoteRequestDialog';
import { detailedServices } from '../data/servicesData';
import { Toaster } from '../components/ui/sonner';

export default function QuotePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [selection, setSelection] = useState(() => {
    const add = params.get('add');
    return add && detailedServices.some((s) => s.slug === add) ? { [add]: 1 } : {};
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Quote Calculator | iWurks360Digital Solutions';
  }, []);

  const toggle = (slug) =>
    setSelection((prev) => {
      const next = { ...prev };
      if (slug in next) delete next[slug];
      else next[slug] = 1;
      return next;
    });

  const setTier = (slug, tierIndex) => setSelection((prev) => ({ ...prev, [slug]: tierIndex }));

  const items = Object.entries(selection).map(([slug, tierIndex]) => {
    const service = detailedServices.find((s) => s.slug === slug);
    return { service, tier: service.tiers[tierIndex] };
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  const goToContactForm = (customer = {}) => {
    const lines = items.map(({ service, tier }) => `${service.title} (${tier.name}) - $${tier.price.toLocaleString()} ${service.billing}`);
    navigate('/', { state: { scrollTo: 'contact', service: 'Custom Quote', quoteLines: lines, customer } });
  };

  const submitQuote = () => setDialogOpen(true);

  return (
    <div className="min-h-screen bg-white" data-testid="quote-page">
      <Header />
      <main>
        <section className="pt-32 sm:pt-40 pb-12 bg-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/services" data-testid="quote-back-link" className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white mb-6">
              <ArrowLeft className="w-4 h-4" /> Services &amp; Pricing
            </Link>
            <div className="flex items-center gap-3 mb-3">
              <Calculator className="w-8 h-8" style={{ color: '#FF9900' }} />
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
                Build your quote
              </h1>
            </div>
            <p className="text-gray-300 text-base md:text-lg max-w-2xl">
              Pick the services you need, choose a package for each, and see your bundled total at 27.5% below market rates, instantly.
            </p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4" data-testid="quote-service-list">
            {detailedServices.map((s) => (
              <QuoteServiceRow
                key={s.slug}
                service={s}
                selected={s.slug in selection}
                tierIndex={selection[s.slug] ?? 1}
                onToggle={() => toggle(s.slug)}
                onTierChange={(i) => setTier(s.slug, i)}
              />
            ))}
          </div>
          <div className="lg:col-span-1">
            <QuoteSummary items={items} onSubmit={submitQuote} />
          </div>
        </section>
      </main>
      <Footer />
      <QuoteRequestDialog open={dialogOpen} onOpenChange={setDialogOpen} items={items} onContactForm={goToContactForm} />
      <ChatBot />
      <Toaster position="top-right" />
    </div>
  );
}
